import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { getProfile, getRiderReputation } from '../../services/profileService';
import { riderTitle } from '../../utils/reputation';
import { isFollowing, followUser, unfollowUser, countFollowers, countFollowing } from '../../services/followService';
import { fetchUserRoutes } from '../../services/publishService';
import { fetchPublishedToursByUser } from '../../services/tourService';
import { ProfileLinks } from '../common/ProfileLinks';
import { GEAR_CAT, affiliatize } from '../../utils/gear';
import { ProfileCalendar } from './ProfileCalendar';
import { CurvinessBadge } from '../common/CurvinessBadge';
import { RouteDetailModal } from '../Community/RouteDetailModal';
import { TourDetailModal } from '../Tours/TourDetailModal';
import { formatDistance } from '../../utils/formatters';
import './UserProfilePanel.css';

export function UserProfilePanel({ uid, fallbackName, fallbackPhoto, onClose, zIndex = 1500 }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [routes, setRoutes]   = useState(null);
  const [tours, setTours]     = useState(null);
  const [selRoute, setSelRoute] = useState(null);
  const [selTour, setSelTour]   = useState(null);
  const [following, setFollowing]   = useState(false);
  const [followers, setFollowers]   = useState(null);
  const [followingN, setFollowingN] = useState(null);
  const [followBusy, setFollowBusy] = useState(false);
  const [gearOpen, setGearOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const [rep, setRep] = useState(null);
  const isSelf = user?.uid === uid;

  function shareGear() {
    const url = `${window.location.origin}/?gear=${uid}`;
    (navigator.clipboard?.writeText(url) ?? Promise.reject())
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); })
      .catch(() => { window.prompt('Copy this link to share the gearlist:', url); });
  }

  useEffect(() => {
    setProfile(null); setRoutes(null); setTours(null); setFollowers(null); setFollowingN(null); setRep(null);
    getProfile(uid).then(setProfile).catch(() => {});
    getRiderReputation(uid).then(setRep).catch(() => {});
    fetchUserRoutes(uid).then(setRoutes).catch(() => setRoutes([]));
    fetchPublishedToursByUser(uid).then(setTours).catch(() => setTours([]));
    countFollowers(uid).then(setFollowers).catch(() => {});
    countFollowing(uid).then(setFollowingN).catch(() => {});
    if (user && user.uid !== uid) isFollowing(user.uid, uid).then(setFollowing).catch(() => {});
    else setFollowing(false);
  }, [uid, user]);

  async function toggleFollow() {
    if (!user || isSelf || followBusy) return;
    setFollowBusy(true);
    try {
      if (following) {
        await unfollowUser(user.uid, uid);
        setFollowing(false); setFollowers(c => Math.max(0, (c || 1) - 1));
      } else {
        await followUser(user, uid);
        setFollowing(true); setFollowers(c => (c || 0) + 1);
      }
    } catch { /* ignore */ } finally {
      setFollowBusy(false);
    }
  }

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const name = profile?.displayName || fallbackName || 'Rider';
  const photo = profile?.photoURL || fallbackPhoto;
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const Row = ({ item, kind }) => {
    const d = kind === 'tour' && item.startDate?.toDate ? item.startDate.toDate() : null;
    return (
      <li className="up-row" onClick={() => kind === 'tour' ? setSelTour(item) : setSelRoute(item)}>
        {item.imageUrls?.[0]
          ? <img className="up-row__thumb" src={item.imageUrls[0]} alt="" loading="lazy" />
          : <div className="up-row__thumb up-row__thumb--ph">{item.name?.[0]?.toUpperCase()}</div>}
        <div className="up-row__info">
          <span className="up-row__name">{item.name}</span>
          <div className="up-row__meta">
            {item.distance != null && <span>{formatDistance(item.distance)}</span>}
            {kind === 'tour'
              ? <span>{d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Date TBD'}</span>
              : <CurvinessBadge geometry={item.geometry} variant="inline" />}
          </div>
        </div>
      </li>
    );
  };

  const loading = routes === null || tours === null;
  const empty = !loading && routes.length === 0 && tours.length === 0;

  const panel = createPortal(
    <div className="up-overlay" style={{ zIndex }} onClick={e => e.target.classList.contains('up-overlay') && onClose()}>
      <aside className="up-panel">
        <div className="up-header">
          <button className="up-close" onClick={onClose} aria-label="Close">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/></svg>
          </button>
        </div>

        <div className="up-id">
          {photo
            ? <img className="up-avatar" src={photo} alt="" />
            : <span className="up-avatar up-avatar--init">{initials}</span>}
          <div className="up-name">{name}</div>
          {rep && <span className="up-title">{riderTitle(rep)}</span>}

          <div className="up-stats">
            <span><strong>{followers ?? '–'}</strong> followers</span>
            <span className="up-stats__dot">·</span>
            <span><strong>{followingN ?? '–'}</strong> following</span>
          </div>

          {/* Verified rider stats — from check-ins, published content, ratings */}
          {rep && (rep.ridesAttended > 0 || rep.ridersGuided > 0 || rep.routesPublished > 0 || rep.toursHosted > 0) && (
            <div className="up-repstrip">
              {rep.ridesAttended > 0 && (
                <span className="up-repstat"><strong>{rep.ridesAttended}</strong> verified {rep.ridesAttended === 1 ? 'ride' : 'rides'}</span>
              )}
              {rep.ridersGuided > 0 && (
                <span className="up-repstat"><strong>{rep.ridersGuided}</strong> riders guided</span>
              )}
              {rep.toursHosted > 0 && (
                <span className="up-repstat"><strong>{rep.toursHosted}</strong> {rep.toursHosted === 1 ? 'tour' : 'tours'} hosted</span>
              )}
              {rep.routesPublished > 0 && (
                <span className="up-repstat"><strong>{rep.routesPublished}</strong> {rep.routesPublished === 1 ? 'route' : 'routes'}</span>
              )}
              {rep.avgRouteRating != null && (
                <span className="up-repstat"><strong>★ {rep.avgRouteRating.toFixed(1)}</strong></span>
              )}
            </div>
          )}

          {user && !isSelf && (
            <button className={`up-follow${following ? ' up-follow--on' : ''}`} onClick={toggleFollow} disabled={followBusy}>
              {following ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Following
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                  Follow
                </>
              )}
            </button>
          )}

          {profile?.bio && <p className="up-bio">{profile.bio}</p>}
          {profile && <ProfileLinks profile={profile} />}
        </div>

        <div className="up-body">
          {profile?.gear?.length > 0 && (
            <div className="up-section">
              <div className="up-gear-head">
                <button className="up-gear-toggle" onClick={() => setGearOpen(o => !o)} aria-expanded={gearOpen}>
                  <svg className={`up-gear-chevron${gearOpen ? ' is-open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  Gear <span className="up-gear-count">{profile.gear.length}</span>
                </button>
                <button className="up-gear-share" onClick={shareGear} title="Copy a shareable link to this gearlist">
                  {copied ? '✓ Copied' : 'Share'}
                </button>
              </div>
              {gearOpen && (
                <ul className="up-gear">
                  {profile.gear.map((g, i) => {
                    const cat = GEAR_CAT[g.category] || GEAR_CAT.other;
                    return (
                      <li className="up-gear__row" key={i}>
                        <span className="up-gear__cat">{cat.icon} {cat.label}</span>
                        <span className="up-gear__name">{g.name}{g.brand ? ` · ${g.brand}` : ''}</span>
                        {g.url && (
                          <a className="up-gear__link" href={affiliatize(g.url)} target="_blank" rel="noopener noreferrer nofollow sponsored">View</a>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {loading && <div className="up-loading"><span className="up-spinner" /> Loading…</div>}
          {empty && <div className="up-empty">No published routes or tours yet.</div>}
          {!loading && tours.length > 0 && (
            <div className="up-section">
              <div className="up-section__title">Tour calendar <span>{tours.length}</span></div>
              <ProfileCalendar events={tours.map(t => ({ ...t, _role: 'tour' }))} />
            </div>
          )}
          {!loading && routes.length > 0 && (
            <div className="up-section">
              <div className="up-section__title">Routes <span>{routes.length}</span></div>
              <ul className="up-list">{routes.map(r => <Row key={r.id} item={r} kind="route" />)}</ul>
            </div>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );

  return (
    <>
      {panel}
      {selRoute && (
        <RouteDetailModal route={selRoute} zIndex={zIndex + 10} onClose={() => setSelRoute(null)} onUnpublished={() => setSelRoute(null)} onExploreUser={() => setSelRoute(null)} />
      )}
      {selTour && (
        <TourDetailModal tour={selTour} zIndex={zIndex + 10} onClose={() => setSelTour(null)} onCancelled={() => setSelTour(null)} />
      )}
    </>
  );
}
